import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Mail, Shield, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { firmInfo } from '../data/mock';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [platformsOpen, setPlatformsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  const platformLinks = [
    { href: '/facebook-account-dispute', label: 'Facebook' },
    { href: '/instagram-account-dispute', label: 'Instagram' },
    { href: '/tiktok-account-dispute', label: 'TikTok' },
    { href: '/youtube-account-dispute', label: 'YouTube' },
    { href: '/x-twitter-account-dispute', label: 'X (Twitter)' },
    { href: '/reddit-account-dispute', label: 'Reddit' },
    { href: '/snapchat-account-dispute', label: 'Snapchat' },
    { href: '/services/ubereats-account-appeal', label: 'Uber Eats' },
  ];

  const navLinks = [
    { href: '/about', label: 'About', isRoute: true },
    { href: '/services', label: 'Services', isRoute: true },
    { href: '/pricing', label: 'Pricing', isRoute: true },
    { href: '/faq', label: 'FAQ', isRoute: true },
    { href: '/blog', label: 'Blog', isRoute: true },
  ];

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setPlatformsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (href) => {
    if (isHomePage) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home with hash
      window.location.assign('/' + href);
    }
    setMobileMenuOpen(false);
  };

  const handleGetStarted = () => {
    if (isHomePage) {
      const element = document.querySelector('#intake');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.location.assign('/#intake');
    }
    setMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a1c1b]/95 backdrop-blur-md border-b border-[#3f4816]/50">
      <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 rounded-full bg-[#d9fb06] flex items-center justify-center transition-transform group-hover:scale-105">
              <Shield className="w-6 h-6 text-[#1a1c1b]" />
            </div>
            <span className="text-xl font-bold text-[#d9fb06] tracking-tight">
              {firmInfo.name}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="desktop-nav hidden lg:flex items-center gap-1">
            {/* Platforms Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setPlatformsOpen(!platformsOpen)}
                className="px-4 py-2 text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors text-sm font-medium flex items-center gap-1"
                data-testid="platforms-dropdown"
              >
                Platforms
                <ChevronDown className={`w-4 h-4 transition-transform ${platformsOpen ? 'rotate-180' : ''}`} />
              </button>
              {platformsOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-[#252825] border border-[#3f4816] rounded-xl shadow-xl py-2 z-50">
                  {platformLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setPlatformsOpen(false)}
                      className="block px-4 py-2 text-[#888680] hover:text-[#d9fb06] hover:bg-[#3f4816]/30 transition-colors text-sm"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="px-4 py-2 text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors text-sm font-medium flex items-center gap-1"
                >
                  {link.icon && <link.icon className="w-4 h-4" />}
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.href}
                  onClick={() => scrollToSection(link.href)}
                  className="px-4 py-2 text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors text-sm font-medium"
                >
                  {link.label}
                </button>
              )
            ))}

            <Link
              to="/notice-of-dispute"
              className="px-4 py-2 text-[#d9fb06] hover:text-[#d9fb06]/80 transition-colors text-sm font-medium"
            >
              Notice of Dispute
            </Link>
          </nav>

          {/* CTA Buttons */}
          <div className="desktop-nav hidden lg:flex items-center gap-4">
            <a
              href={`mailto:${firmInfo.email}`}
              className="flex items-center gap-2 text-[#888680] hover:text-[#d9fb06] transition-colors text-sm"
            >
              <Mail className="w-4 h-4" />
              {firmInfo.email}
            </a>
            <Button
              onClick={handleGetStarted}
              className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-6 font-semibold uppercase text-sm tracking-tight"
              data-testid="header-get-started"
            >
              Get Started
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden p-2 text-[#d9fb06]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-[#3f4816]/50 max-h-[80vh] overflow-y-auto">
            <nav className="flex flex-col gap-1">
              {/* Platforms Section */}
              <div className="px-4 py-2 text-[#888680] text-xs uppercase tracking-wider">Platforms</div>
              {platformLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-6 py-2 text-[#d9fb06] hover:bg-[#3f4816]/30 transition-colors text-sm"
                >
                  {link.label}
                </Link>
              ))}
              
              <div className="border-t border-[#3f4816]/50 my-2"></div>
              
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-3 text-[#d9fb06] hover:bg-[#3f4816]/30 transition-colors font-medium"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <button
                    key={link.href}
                    onClick={() => scrollToSection(link.href)}
                    className="px-4 py-3 text-[#d9fb06] hover:bg-[#3f4816]/30 transition-colors text-left font-medium"
                  >
                    {link.label}
                  </button>
                )
              ))}
              
              <Link
                to="/notice-of-dispute"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-[#d9fb06] hover:bg-[#3f4816]/30 transition-colors font-medium"
              >
                Notice of Dispute
              </Link>
              
              <Link
                to="/platform-registered-agent-addresses"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 text-[#d9fb06] hover:bg-[#3f4816]/30 transition-colors font-medium"
              >
                Registered Agents
              </Link>
              
              <div className="pt-4 px-4 flex flex-col gap-3">
                <a
                  href={`mailto:${firmInfo.email}`}
                  className="flex items-center gap-2 text-[#888680] text-sm"
                >
                  <Mail className="w-4 h-4" />
                  {firmInfo.email}
                </a>
                <Button
                  onClick={handleGetStarted}
                  className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full font-semibold uppercase w-full"
                >
                  Get Started
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
